import type { CompanyBilling, NextPayment } from '@vlm/api';
import { useQuery } from '@tanstack/react-query';
import { companyApi } from './api';
import { ADMINS, useCan } from './auth';
import { today } from './time';

/** Everything about the signed-in user's own company; signing in or out clears it with the rest. */
export const COMPANY_KEY = ['company'] as const;

/** The company's name, plan and how full the plan is — every role reads it (the shell shows it). */
export function useCompany() {
  return useQuery({ queryKey: [...COMPANY_KEY, 'summary'], queryFn: () => companyApi.summary(), staleTime: 60_000 });
}

/** Plan, next payment and the bank to pay — for the company's ADMINs (the API refuses the others). */
export function useBilling() {
  const admin = useCan(ADMINS);
  return useQuery({ queryKey: [...COMPANY_KEY, 'billing'], queryFn: () => companyApi.billing(), enabled: admin, staleTime: 60_000 });
}

export function useInvoices() {
  const admin = useCan(ADMINS);
  return useQuery({ queryKey: [...COMPANY_KEY, 'invoices'], queryFn: () => companyApi.invoices(), enabled: admin });
}

/**
 * What the company should hear about paying, if anything: `overdue` once an unpaid invoice is
 * past its due date or the paid period has ended (the API's PAST_DUE), `first` while the plan
 * waits for its first payment — not during a free trial (`onTrial`), when the plan only starts
 * after it. Billing days are the fleet's (Riyadh) calendar days.
 */
export type PaymentState = { kind: 'overdue' | 'first'; next: NextPayment } | null;

export function paymentState(billing: CompanyBilling | undefined, day = today(), onTrial = false): PaymentState {
  const next = billing?.nextPayment;
  if (!next) return null;
  const status = billing.subscription?.status;
  if (next.date < day || status === 'PAST_DUE') return { kind: 'overdue', next };
  if (status === 'PENDING' && !onTrial) return { kind: 'first', next };
  return null;
}
